// Pure, dependency-free so it can be tested outside the browser.

// Page size for server-side pagination — all roles use the same approach now.
// The backend handles owner filtering, household exclusion, and pagination.
export const DEFAULT_PAGE_SIZE = 50;

export type UnassignedLeadsFilters = {
  state?: string;
  states?: string;            // comma-separated multi-state — server-side filter
  timezone?: string;          // EST|CST|MST|PST|AKST|HST — server-side filter
  listName?: string;
  page?: number;
  limit?: number;
  assignmentStatus?: string;  // 'assigned' | 'unassigned' | 'all' — server-side filter
  filterAgentId?: string;     // Filter by specific agent — server-side filter
  status?: string;            // Disposition/leadStat — server-side filter
  leadName?: string;          // Search by lead name — server-side filter
  filterAgencyId?: string;    // Filter by assigned sub-agency — server-side filter
  householdPrimaries?: boolean; // Show only household primaries — server-side filter
  city?: string;               // Filter by city — server-side filter
  carrier?: string;            // Filter by carrier — server-side filter
  callRecency?: string;        // 'never' | 'stale' — InsuraBot smart-filter segment
  minAge?: string;             // whole years, inclusive — server-side filter
  maxAge?: string;
  customFieldKey?: string;     // custom-field name — server-side filter
  customFieldValues?: string;  // pipe-separated values — server-side filter
  customFieldMin?: string;     // numeric range min on custom field — server-side filter
  customFieldMax?: string;     // numeric range max on custom field — server-side filter
  // Additional AND-ed custom-field conditions, JSON-encoded. Lets one filter
  // express Carrier + Product Type + Premium instead of needing a saved
  // pipeline per combination.
  customFieldFilters?: string;
};

/**
 * The ONE place the lead-assignment filters become query params. The page list
 * and "Select first N / Select all" both call this; they used to build params
 * separately and drifted (select-all silently dropped the extra custom-field
 * conditions), so a selection could include leads the list never showed.
 */
export function buildUnassignedLeadsParams(filters: UnassignedLeadsFilters | undefined, isOwnerOrAdmin: boolean): URLSearchParams {
  const queryParams = new URLSearchParams();
  if (filters?.state) queryParams.append('state', filters.state);
  if (filters?.states) queryParams.append('states', filters.states);
  if (filters?.timezone) queryParams.append('timezone', filters.timezone);
  if (filters?.listName) queryParams.append('listName', filters.listName);
  if (filters?.status) queryParams.append('status', filters.status);
  if (filters?.assignmentStatus && filters.assignmentStatus !== 'all') {
    queryParams.append('assignmentStatus', filters.assignmentStatus);
  }
  if (filters?.filterAgentId) queryParams.append('filterAgentId', filters.filterAgentId);
  if (filters?.leadName) queryParams.append('leadName', filters.leadName);
  if (filters?.filterAgencyId) queryParams.append('filterAgencyId', filters.filterAgencyId);
  if (filters?.householdPrimaries) queryParams.append('householdPrimaries', 'true');
  if (filters?.city) queryParams.append('city', filters.city);
  if (filters?.carrier) queryParams.append('carrier', filters.carrier);
  if (filters?.minAge) queryParams.append('minAge', filters.minAge);
  if (filters?.maxAge) queryParams.append('maxAge', filters.maxAge);
  if (filters?.callRecency) queryParams.append('callRecency', filters.callRecency);
  if (filters?.customFieldKey && filters?.customFieldValues) {
    queryParams.append('customFieldKey', filters.customFieldKey);
    queryParams.append('customFieldValues', filters.customFieldValues);
  }
  if (filters?.customFieldFilters) {
    queryParams.append('customFieldFilters', filters.customFieldFilters);
  }
  if (filters?.customFieldKey && (filters?.customFieldMin || filters?.customFieldMax)) {
    queryParams.append('customFieldKey', filters.customFieldKey);
    if (filters.customFieldMin) queryParams.append('customFieldMin', filters.customFieldMin);
    if (filters.customFieldMax) queryParams.append('customFieldMax', filters.customFieldMax);
  }
  queryParams.append('page', String(filters?.page || 1));
  queryParams.append('limit', String(filters?.limit || DEFAULT_PAGE_SIZE));
  // Owners/admins see both assigned + unassigned so they can re-assign.
  // Agents only see leads assigned to them.
  queryParams.append('includeAssigned', isOwnerOrAdmin ? 'true' : 'false');
  return queryParams;
}
